import { PERIODISATION_OPTIONS } from "../types/post";

/**
 * Colour system for the Schedule screens.
 *
 * Two independent, athlete-controllable palettes:
 *  - Training-type colours   → dot + type label on session cards
 *  - Periodisation colours   → week bars in the Year view
 *
 * Defaults are provided here; per-athlete overrides live on the user doc
 * (`trainingTypeColors` / `periodisationColors`) and are merged in via the
 * resolver helpers below.
 */

export type ColorMap = Record<string, string>;

/** Ordered fallback swatches used to auto-assign a colour to any key without an explicit value. */
export const SCHEDULE_SWATCHES: string[] = [
  "#0096c7", // primary blue
  "#22c55e", // green
  "#f97316", // orange
  "#a855f7", // purple
  "#14b8a6", // teal
  "#ec4899", // pink
  "#eab308", // amber
  "#3b82f6", // blue
  "#84cc16", // lime
  "#ef4444", // red
  "#06b6d4", // cyan
  "#8b5cf6", // violet
];

/** Default colours for the 9 built-in periodisation phases. */
export const DEFAULT_PERIODISATION_COLORS: ColorMap = {
  accumulation: "#3b82f6",
  building: "#22c55e",
  deload: "#a855f7",
  familiarisation: "#14b8a6",
  intensification: "#f97316",
  maintenance: "#84cc16",
  prehab: "#eab308",
  rehab: "#a8a29e",
  taper: "#ec4899",
};

const PERIODISATION_LABELS: ColorMap = PERIODISATION_OPTIONS.reduce(
  (acc, opt) => {
    acc[opt.value] = opt.label;
    return acc;
  },
  {} as ColorMap,
);

/** Stable hash so an unmapped key always resolves to the same swatch. */
const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

/** Deterministically pick a fallback swatch for a key. */
export const swatchForKey = (key: string): string =>
  SCHEDULE_SWATCHES[hashString(key) % SCHEDULE_SWATCHES.length];

/**
 * Resolve the colour for a training type.
 * @param key A stable identifier (trainingTypeID preferred, else the name).
 * @param overrides The athlete's `trainingTypeColors` map.
 */
export const resolveTrainingTypeColor = (
  key: string | null | undefined,
  overrides?: ColorMap,
): string => {
  if (!key) return "#0096c7";
  if (overrides?.[key]) return overrides[key];
  return swatchForKey(key);
};

/**
 * Resolve the colour for a periodisation phase.
 * @param phase The periodisation phase value (built-in or custom string).
 * @param overrides The athlete's `periodisationColors` map.
 */
export const resolvePeriodisationColor = (
  phase: string | null | undefined,
  overrides?: ColorMap,
): string => {
  if (!phase) return "#e5e7eb";
  if (overrides?.[phase]) return overrides[phase];
  if (DEFAULT_PERIODISATION_COLORS[phase])
    return DEFAULT_PERIODISATION_COLORS[phase];
  return swatchForKey(phase);
};

/** Human-friendly label for a periodisation phase (falls back to Title Case). */
export const periodisationLabel = (phase: string): string => {
  if (PERIODISATION_LABELS[phase]) return PERIODISATION_LABELS[phase];
  return phase.charAt(0).toUpperCase() + phase.slice(1);
};
